// Copied from namat-mobile src/lib/firebase/queries/competitionRanking.ts so web,
// mobile and the server (functions/notifications/ranking.js) rank the same way.
// Change all three together (namat#29/#32).
/**
 * Pure resolution logic for competition group/section rankings — no Firestore
 * imports here, so this stays trivially unit-testable. The async Firestore
 * fetch/cache layer lives in competitions.ts and feeds this module raw data.
 *
 * Settled product rules (do not reinterpret):
 * - Sections are conceptual, not joinable: a user's section is DERIVED from
 *   their subgroup's parentGroupId. Direct membership in a
 *   groupType === 'parent' doc is an artifact of live data and is IGNORED.
 * - A user has at most one subgroup per organization (enforced on every
 *   join/add path, namat#30); if legacy data still holds several, the user
 *   counts for the one they joined FIRST (earliest joinedAt, then lowest
 *   group id) — deterministic, never arbitrary query order.
 * - Org-less groups and parent-less subgroups are legal states, not bugs.
 *   An org-less group is out of competition scope; a subgroup with no
 *   parent means the user has NO section (never a fabricated one).
 */

export interface GroupMemberRecord {
  userId: string;
  groupId: string;
  /** The row's joinedAt in epoch ms (toJoinedAtMillis); missing sorts last. */
  joinedAtMs?: number;
}

/**
 * A groupMembers row's joinedAt as epoch ms, whatever shape it was read in
 * (Firestore Timestamp from the client or admin SDK, Date, ISO string, ms
 * number, or a plain {seconds} map). undefined when absent or unreadable.
 */
export function toJoinedAtMillis(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  let ms: number | undefined;
  if (typeof value === 'number') ms = value;
  else if (typeof value === 'string') ms = Date.parse(value);
  else if (value instanceof Date) ms = value.getTime();
  else if (typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    ms = (value as { toMillis: () => number }).toMillis();
  } else if (typeof (value as { seconds?: unknown }).seconds === 'number') {
    const v = value as { seconds: number; nanoseconds?: number };
    ms = v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  }
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : undefined;
}

export interface SupportGroupRecord {
  id: string;
  organization?: string;
  groupType?: string;
  parentGroupId?: string;
  name?: string;
}

export interface UserContainerResolution {
  groupId: string | null;
  sectionId: string | null;
}

/**
 * A user's GROUP for a competition scoped to `orgId`: the `supportGroups`
 * doc they have a `groupMembers` row for, that (a) belongs to this org and
 * (b) is not a `parent` doc (a direct parent membership is an artifact and
 * must be ignored for ranking purposes). If legacy data has several that
 * qualify (namat#30), pick the one joined first — earliest joinedAt, rows
 * without one last, ties by lowest group id — so the user counts for exactly
 * one group, the same one everywhere.
 */
export function resolveUserGroupId(
  userId: string,
  orgId: string,
  memberRows: GroupMemberRecord[],
  groupsById: Map<string, SupportGroupRecord>
): string | null {
  const joinedAt = new Map<string, number>();
  for (const m of memberRows) {
    if (m.userId !== userId) continue;
    const g = groupsById.get(m.groupId);
    if (!g || g.organization !== orgId || g.groupType === 'parent') continue;
    const at = typeof m.joinedAtMs === 'number' && Number.isFinite(m.joinedAtMs) ? m.joinedAtMs : Infinity;
    joinedAt.set(m.groupId, Math.min(joinedAt.get(m.groupId) ?? Infinity, at));
  }
  if (joinedAt.size === 0) return null;
  const [first] = Array.from(joinedAt.entries()).sort(
    ([a, aAt], [b, bAt]) => (aAt === bAt ? 0 : aAt - bAt) || (a < b ? -1 : a > b ? 1 : 0)
  );
  return first[0];
}

/**
 * A user's SECTION: their resolved group's `parentGroupId`, ONLY if that id
 * refers to an existing `supportGroups` doc that is itself `groupType ===
 * 'parent'` in the SAME org. No self-fallback — a subgroup with no parent,
 * or whose parent is missing/foreign-org, means the user has no section.
 */
export function resolveSectionId(
  groupId: string | null,
  orgId: string,
  groupsById: Map<string, SupportGroupRecord>
): string | null {
  if (!groupId) return null;
  const group = groupsById.get(groupId);
  if (!group) return null;
  const parentId = group.parentGroupId;
  if (!parentId) return null;
  const parent = groupsById.get(parentId);
  if (!parent) return null;
  if (parent.groupType !== 'parent') return null;
  if (parent.organization !== orgId) return null;
  return parentId;
}

/** Combines the two resolutions above for a single user. */
export function resolveUserContainer(
  userId: string,
  orgId: string,
  memberRows: GroupMemberRecord[],
  groupsById: Map<string, SupportGroupRecord>
): UserContainerResolution {
  const groupId = resolveUserGroupId(userId, orgId, memberRows, groupsById);
  const sectionId = resolveSectionId(groupId, orgId, groupsById);
  return { groupId, sectionId };
}

/** Resolves every user in `userIds` once, sharing the same member/group data. */
export function resolveAllUserContainers(
  userIds: string[],
  orgId: string,
  memberRows: GroupMemberRecord[],
  groupsById: Map<string, SupportGroupRecord>
): Map<string, UserContainerResolution> {
  const out = new Map<string, UserContainerResolution>();
  for (const uid of userIds) {
    out.set(uid, resolveUserContainer(uid, orgId, memberRows, groupsById));
  }
  return out;
}

/** One decimal, no trailing ".0": 2.5, 3. */
export const formatAverage = (average: number): number => Math.round(average * 10) / 10;

export interface ContainerStandingRow {
  id: string;
  name: string;
  completed: number;
  memberCount: number;
  /** completed / memberCount — the ranking score (namat#32); 0 for an empty container. */
  average: number;
  rank: number;
  isMe: boolean;
}

/**
 * A membership is a beneficiary only if its account still exists and holds an
 * active beneficiary (customer) role (namat#87): deleted accounts leave their
 * organizationMembers rows behind. Same rule as the web manager list.
 */
export function isActiveBeneficiary(user: unknown): boolean {
  const roles = (user as { roles?: { role?: string; isActive?: boolean }[] } | undefined)?.roles;
  return Array.isArray(roles) && roles.some((r) => r?.role === 'customer' && r?.isActive === true);
}

/**
 * Drops everyone who is not a beneficiary from both the roster and the people
 * who logged, before withParticipants. Applied identically by web, mobile and
 * the server so ranks and rank-move pushes agree.
 */
export function restrictToBeneficiaries(
  perUser: Array<{ userId: string; completed: number }>,
  participantIds: string[],
  isBeneficiary: (userId: string) => boolean
): { perUser: Array<{ userId: string; completed: number }>; participantIds: string[] } {
  return {
    perUser: perUser.filter((r) => isBeneficiary(r.userId)),
    participantIds: participantIds.filter((id) => isBeneficiary(id)),
  };
}

/**
 * Everyone in the competition, not just those who logged (namat#29): adds a
 * zero row for each roster member missing from `perUser`. Zero rows sort below
 * everyone who logged, so the ranks of people who logged are unchanged (the
 * server's rank-move pushes stay valid). Same tie-break as everywhere else.
 */
export function withParticipants(
  perUser: Array<{ userId: string; completed: number }>,
  participantIds: string[]
): Array<{ userId: string; completed: number }> {
  const seen = new Set(perUser.map((r) => r.userId));
  const zeros = Array.from(new Set(participantIds))
    .filter((id) => !seen.has(id))
    .map((userId) => ({ userId, completed: 0 }));
  return sortByCompletedThenUserId([...perUser, ...zeros]);
}

/**
 * Container order (namat#32): completions per member, then the higher total,
 * then the larger group, then id. Must match the server's buildContainerRankMap
 * exactly, or a rank-move push contradicts the screen.
 */
export function compareContainers(
  a: { id: string; completed: number; memberCount: number; average: number },
  b: { id: string; completed: number; memberCount: number; average: number }
): number {
  return (
    b.average - a.average ||
    b.completed - a.completed ||
    b.memberCount - a.memberCount ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/**
 * Pure aggregation: sums per-user completions into whichever container each
 * user resolved to (group or section id), then ranks ALL known containers
 * (including zero-completion ones — web parity) highest-first. Users who
 * resolved to no container (org-less group, parent-only membership, no
 * parent, etc.) simply contribute to no bucket — they still count in the
 * separate individual ranking, never fabricated into one here.
 */
export function buildContainerStandingsPure(
  currentUserId: string,
  topN: number,
  containers: { id: string; name: string }[],
  userContainerId: Map<string, string | null>,
  perUser: Array<{ userId: string; completed: number }>
): { rows: ContainerStandingRow[]; me: ContainerStandingRow | null; total: number } {
  const valid = new Set(containers.map((c) => c.id));
  const nameOf = new Map(containers.map((c) => [c.id, c.name]));

  const completed = new Map<string, number>();
  const members = new Map<string, Set<string>>();
  for (const { userId, completed: c } of perUser) {
    const cid = userContainerId.get(userId);
    if (!cid || !valid.has(cid)) continue;
    completed.set(cid, (completed.get(cid) || 0) + c);
    if (!members.has(cid)) members.set(cid, new Set());
    members.get(cid)!.add(userId);
  }

  const ranking: ContainerStandingRow[] = containers
    .map((c) => {
      const total = completed.get(c.id) || 0;
      const memberCount = members.get(c.id)?.size || 0;
      return {
        id: c.id,
        name: nameOf.get(c.id) || 'Unknown',
        completed: total,
        memberCount,
        average: memberCount > 0 ? total / memberCount : 0,
        rank: 0,
        isMe: false,
      };
    })
    .sort(compareContainers)
    .map((c, i) => ({ ...c, rank: i + 1 }));

  const myId = userContainerId.get(currentUserId) || null;
  const rows = ranking.slice(0, topN).map((c) => ({ ...c, isMe: c.id === myId }));
  const meRow = myId ? ranking.find((c) => c.id === myId) : undefined;
  const me = meRow ? { ...meRow, isMe: true } : null;

  return { rows, me, total: ranking.length };
}

/**
 * Deterministic tie-break for per-user completion ranking: highest completed
 * first, ties broken by ascending userId. Must match the server's identical
 * tie-break (web/functions/notifications/ranking.js) exactly — Map/document
 * iteration order is NOT a substitute, since it differs between the app's
 * document-iteration order and the server's Firestore map-key order for the
 * exact same underlying data. Without a shared tie-break, two customers on
 * equal counts get opposite ranks depending on which side computed them: the
 * push says "now #4" while this screen says 5th.
 */
export function sortByCompletedThenUserId(
  perUser: Array<{ userId: string; completed: number }>
): Array<{ userId: string; completed: number }> {
  return [...perUser].sort((a, b) => {
    if (b.completed !== a.completed) return b.completed - a.completed;
    if (a.userId < b.userId) return -1;
    if (a.userId > b.userId) return 1;
    return 0;
  });
}

export interface ContainerMemberRow {
  userId: string;
  completed: number;
  rank: number;
  isMe: boolean;
}

/**
 * Pure derivation of ONE container's (a group or a section) full member
 * leaderboard from the exact same perUser + userContainerId inputs
 * buildContainerStandingsPure already aggregates from — no new Firestore
 * reads, no name resolution (names are looked up separately, batched, only
 * for the members this returns). Filters perUser down to whoever resolved
 * into `containerId`, then ranks them highest-completed-first with the same
 * stable-sort / positional-rank convention used everywhere else in this
 * module and in getCompetitionStandings: ties keep perUser's relative order,
 * ranks are a plain 1..N sequence (never a shared/dense rank for ties).
 */
export function buildContainerMemberRankingPure(
  currentUserId: string,
  containerId: string,
  userContainerId: Map<string, string | null>,
  perUser: Array<{ userId: string; completed: number }>
): ContainerMemberRow[] {
  const members = perUser.filter((r) => userContainerId.get(r.userId) === containerId);
  return members
    .slice()
    .sort((a, b) => b.completed - a.completed)
    .map((r, i) => ({
      userId: r.userId,
      completed: r.completed,
      rank: i + 1,
      isMe: r.userId === currentUserId,
    }));
}
